import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    AccountBalance as BalanceIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { compensationService } from '../services/api';
import StarSystem from './dashboard/StarSystem';

interface CompensationData {
    balance_deductivo: number;
    unrealized_profit: number;
    realized_profit: number;
    updated_at: string;
}

const CompensationSystem: React.FC = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const [compensationData, setCompensationData] = useState<CompensationData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const t = (es: string, en: string) => language === 'es' ? es : en;

    const fetchCompensationData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const data = await compensationService.getCompensationData();
            setCompensationData(data);
        } catch (err) {
            console.error('Error fetching compensation data:', err);
            setError(err instanceof Error ? err.message : t('Error desconocido', 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompensationData();
    }, []);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'TFT',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    if (loading) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="60vh"
                flexDirection="column"
                gap={2}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" color="textSecondary">
                    {t('Cargando sistema de compensación...', 'Loading compensation system...')}
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Box display="flex" justifyContent="center" gap={2}>
                    <button 
                        onClick={fetchCompensationData}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                    >
                        {t('Reintentar', 'Retry')}
                    </button>
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                    >
                        {t('Volver al Dashboard', 'Back to Dashboard')}
                    </button>
                </Box>
            </Container>
        );
    }

    if (!compensationData) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="info">
                    {t('No se encontraron datos de compensación', 'No compensation data found')}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
                    {t('Sistema de Compensación Tfiats', 'Tfiats Compensation System')}
                </Typography>
                <Tooltip title={t('Actualizar datos', 'Refresh data')}>
                    <IconButton onClick={fetchCompensationData} color="primary">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Resumen de Balances */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={4}>
                    <Card elevation={3}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <BalanceIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    {t('Balance Deductivo', 'Deductive Balance')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="primary" fontWeight="bold">
                                {formatCurrency(compensationData.balance_deductivo)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Card elevation={3}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    {t('Ganancias Realizadas', 'Realized Profits')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="success.main" fontWeight="bold">
                                {formatCurrency(compensationData.realized_profit)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Card elevation={3}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <TrendingDownIcon color="warning" sx={{ mr: 1 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    {t('Ganancias No Realizadas', 'Unrealized Profits')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="warning.main" fontWeight="bold">
                                {formatCurrency(compensationData.unrealized_profit)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>




            {/* Información de actualización */}
            <Box mt={3} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                    {t('Última actualización', 'Last updated')}: {formatDate(compensationData.updated_at)}
                </Typography>
            </Box>

            {/* Sistema de Estrellas Calificadas */}
            <Paper elevation={3} sx={{ mb: 4 }}>
                <Box p={3}>
                    <Typography variant="h5" fontWeight="bold" mb={3}>
                        {t('Sistema de Estrellas Calificadas', 'Qualified Stars System')}
                    </Typography>
                    <StarSystem />
                </Box>
            </Paper>

            {/* Botón de regreso */}
            <Box mt={4} display="flex" justifyContent="center">
                <button 
                    onClick={() => navigate('/dashboard')}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                >
                    {t('Volver al Dashboard', 'Back to Dashboard')}
                </button>
            </Box>
        </Container>
    );
};

export default CompensationSystem; 